import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, WhaleuRules, WhaleuStats } from '../../lib/api';

const AUDIENCES = ['新生', '老生', '研究生', '全校'];
const TOPICS = ['讲座', '教务', '招聘', '竞赛', '奖学金'];
const EMPTY_RULES: WhaleuRules = { audience: [], college: [], topic: [], deliver: true };

interface WhaleuPreferencesProps {
  writeAllowed: boolean;
  onProfileSynced: () => Promise<void>;
}

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export function WhaleuPreferences({ writeAllowed, onProfileSynced }: WhaleuPreferencesProps) {
  const [rules, setRules] = useState<WhaleuRules>(EMPTY_RULES);
  const [stats, setStats] = useState<WhaleuStats>();
  const [collegeText, setCollegeText] = useState('');
  const [status, setStatus] = useState('正在读取订阅设置…');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [subscription, statsResult] = await Promise.allSettled([
      api<{ data: { rules: WhaleuRules } }>('/api/v1/whaleu/subscription'),
      api<{ data: WhaleuStats }>('/api/v1/whaleu/stats'),
    ]);
    if (subscription.status === 'fulfilled') {
      const next = subscription.value.data.rules || EMPTY_RULES;
      setRules(next);
      setCollegeText(next.college.join('、'));
      setStatus('');
    } else {
      setStatus(subscription.reason instanceof Error ? subscription.reason.message : '订阅设置暂时不可用');
    }
    if (statsResult.status === 'fulfilled') setStats(statsResult.value.data);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const sharedServiceAvailable = stats?.backend === 'postgres' && stats.available;
  const canSave = writeAllowed && sharedServiceAvailable && !busy;
  const setDimension = (key: 'audience' | 'topic', value: string) => {
    setRules((current) => ({ ...current, [key]: toggleValue(current[key], value) }));
  };

  const save = async () => {
    setBusy(true);
    setStatus('正在保存…');
    try {
      const college = collegeText.split(/[,，、]/).map((item) => item.trim()).filter(Boolean);
      const response = await api<{ data: { rules: WhaleuRules } }>('/api/v1/whaleu/subscription', {
        method: 'PUT', body: JSON.stringify({ rules: { ...rules, college } }),
      });
      setRules(response.data.rules);
      setCollegeText(response.data.rules.college.join('、'));
      setStatus('信库订阅已保存。');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : '订阅保存失败');
    } finally { setBusy(false); }
  };

  const applyStoredProfile = async () => {
    setBusy(true);
    setStatus('正在使用已有学籍更新画像与订阅…');
    try {
      await api('/api/v1/whaleu/profile/sync', { method: 'POST' });
      await Promise.all([load(), onProfileSynced()]);
      setStatus('已使用账户中现有的学籍记录更新画像与默认订阅。');
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) setStatus('账户中还没有学籍记录。请先手动填写；自动同步需要尚未发布的本地同步器。');
      else setStatus(error instanceof Error ? error.message : '画像同步失败');
    } finally { setBusy(false); }
  };

  return <section className="paper-card whaleu-preferences" id="whaleu-subscription">
    <header><div><small>WHALEU · SIGNAL FILTER</small><h3>校园信库订阅</h3><p>让与你有关的通知进入收件箱，其余信息留在信库里。</p></div><i className={rules.deliver ? 'enabled' : ''}>{rules.deliver ? '投递中' : '已暂停'}</i></header>
    <fieldset disabled={!writeAllowed || busy}>
      <legend>适用人群</legend>
      <div className="subscription-chips">{AUDIENCES.map((item) => <button type="button" key={item} aria-pressed={rules.audience.includes(item)} onClick={() => setDimension('audience', item)}>{item}</button>)}</div>
    </fieldset>
    <fieldset disabled={!writeAllowed || busy}>
      <legend>关注主题</legend>
      <div className="subscription-chips">{TOPICS.map((item) => <button type="button" key={item} aria-pressed={rules.topic.includes(item)} onClick={() => setDimension('topic', item)}>{item}</button>)}</div>
    </fieldset>
    <label>学院范围<input disabled={!writeAllowed || busy} value={collegeText} onChange={(event) => setCollegeText(event.target.value)} placeholder="例如：软件学院、计算机科学与技术系" /><small>可填写多个学院，用顿号分隔；留空表示不限学院。</small></label>
    <button className="subscription-delivery" type="button" disabled={!writeAllowed || busy} onClick={() => setRules((current) => ({ ...current, deliver: !current.deliver }))}><span><b>投递到通知收件箱</b><small>只投递命中以上规则的新信息</small></span><i className={rules.deliver ? 'on' : ''} /></button>
    {!sharedServiceAvailable && stats ? <p className="subscription-boundary">当前仅连接本机只读信库。浏览与检索可用，订阅保存和自动投递需配置 PostgreSQL 共享服务。</p> : null}
    <div className="subscription-actions"><button className="quiet-button" type="button" disabled={!writeAllowed || busy} onClick={applyStoredProfile}>用已有学籍更新订阅</button><button className="primary-button" type="button" disabled={!canSave} onClick={save}>保存订阅</button></div>
    {status ? <p className="subscription-status" role="status">{status}</p> : null}
  </section>;
}
